import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VisuallyHidden,
  useDisclosure,
} from "@chakra-ui/react";
import { FiInbox, FiPlus, FiRotateCcw, FiSearch } from "react-icons/fi";
import { useQuery } from "@tanstack/react-query";

import CreateApplicationModal from "../components/createApplicationModal";
import { axiosClient } from "../client/http-client";
import { RegisterBranchApplicationViewModal } from "../components/RegisterBranch/applicationViewModal";
import { RowActions, clickableRowStyles } from "../components/applicationRowActions";
import StockAnalysis from "../components/Analysis/stockAnalysis";
import SpendAnalysis from "../components/Analysis/spendAnalysis";
import {
  DataTable,
  EmptyState,
  formatShortDate,
  getStatusMeta,
  Highlight,
  MotionReveal,
  PageHeader,
  Pagination,
  SearchField,
  SectionCard,
  StatCard,
  StatSkeleton,
  StatusBadge,
  TableSkeleton,
} from "../components/ui";
import { useUserData } from "../data/userContext";
import { SpendAnalysisItem, StockAnalysisItem } from "../types/analysisDataTypes";
import { formatAmount } from "../utils/formatNumber";

// NetSuite rebuilds the analysis daily, so don't refetch it on every focus
const ANALYSIS_STALE_TIME = 5 * 60 * 1000;

interface ApplicationsData {
  registerbranch: Array<ApplicationData>;
  requestcredit: Array<ApplicationData>;
}

interface ApplicationData {
  id: number;
  account_id: string;
  branch_id: string;
  application_id: string;
  status: string;
  creation_date: string;
  last_update: string;
  outlet_legal_name: string;
}

const ACTIONS_COLUMN = "Actions";
const REGISTER_BRANCH_COLUMNS = ["Application ID", "Branch Name", "Status", "Creation Date", "Last Update", ACTIONS_COLUMN];

/** Sunken sub-panel header: h3 + count chip (a skeleton chip while loading, so it never shows a false "0"). */
const SubPanelHeader: React.FC<{ title: string; count: number; isLoading?: boolean }> = ({ title, count, isLoading = false }) => (
  <Flex align="center" gap={3} mb={4}>
    <Heading as="h3" size="sm">
      {title}
    </Heading>
    {isLoading ? (
      <Skeleton speed={1.2} as="span" display="inline-block" h="1.125rem" w="1.75rem" borderRadius="full" />
    ) : (
      <Box
        as="span"
        textStyle="caption"
        bg="surface.card"
        color="text.secondary"
        borderRadius="full"
        px={2}
        py="1px"
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {count}
        <VisuallyHidden> in total</VisuallyHidden>
      </Box>
    )}
  </Flex>
);

const ApplicationsPage: React.FC = () => {
  const userData = useUserData();
  const createApplication = useDisclosure();

  const [filteredApplications, setFilteredApplications] =
    useState<ApplicationsData>();
  const [searchTerm, setSearchTerm] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const [pageForRB, setPageForRB] = useState(1);
  const itemsPerPage = 5;

  // application view modal states
  const {
    isOpen: isRegisterBranchModalOpen,
    onOpen: onRegisterBranchModalOpen,
    onClose: onRegisterBranchModalClose,
  } = useDisclosure();
  const [selectedApplication, setSelectedApplication] =
    useState<ApplicationData | null>(null);

  // applications Data
  const getApplications = async () => {
    const { data } = await axiosClient.get("/applications");
    return data;
  };

  const {
    data: ApplicationsData,
    isPending: isApplicationDataPending,
    isError: isApplicationsError,
    refetch: refetchApplications,
  } =
    useQuery({
      queryKey: ["applications"],
      queryFn: getApplications
    });

  // financial details data
  const getFinancialDetailsData = async () => {
    const { data } = await axiosClient.get(
      "/contracts/getCustomerStatementDetails");
    return data.data;
  };

  const {
    data: FinancialDetailsData,
    isPending: isFinancialDetailsPending,
    isError: isFinancialError,
    refetch: refetchFinancial,
  } =
    useQuery({
      queryKey: ["financialDetails"],
      queryFn: getFinancialDetailsData,
    });

  // stock & spend analysis data (data is null when the customer has no orders).
  // A NetSuite failure here must not log the user out, the sections show an error.
  const getStockAnalysisData = async (): Promise<StockAnalysisItem[]> => {
    const { data } = await axiosClient.get("/contracts/stockAnalysis", {
      skipAuthRedirect: true,
    });
    return data.data ?? [];
  };

  const stockAnalysis = useQuery({
    queryKey: ["stockAnalysis"],
    queryFn: getStockAnalysisData,
    staleTime: ANALYSIS_STALE_TIME,
    retry: 1,
  });

  const getSpendAnalysisData = async (): Promise<SpendAnalysisItem[]> => {
    const { data } = await axiosClient.get("/contracts/spendAnalysis", {
      skipAuthRedirect: true,
    });
    return data.data ?? [];
  };

  const spendAnalysis = useQuery({
    queryKey: ["spendAnalysis"],
    queryFn: getSpendAnalysisData,
    staleTime: ANALYSIS_STALE_TIME,
    retry: 1,
  });

  const filterApplications = (applications: ApplicationsData, term: string) => {
    const lowerCaseTerm = term.toLowerCase();

    // Matches the raw values (ISO dates, API status) and the forms the table displays (dd/mm/yyyy, badge label).
    const filterList = (list: ApplicationData[]) =>
      list.filter(
        (app) =>
          app.application_id.toLowerCase().includes(lowerCaseTerm) ||
          app.status.toLowerCase().includes(lowerCaseTerm) ||
          app.creation_date.toLowerCase().includes(lowerCaseTerm) ||
          app.last_update.toLowerCase().includes(lowerCaseTerm) ||
          formatShortDate(new Date(app.creation_date)).includes(lowerCaseTerm) ||
          formatShortDate(new Date(app.last_update)).includes(lowerCaseTerm) ||
          getStatusMeta("application", app.status).label.toLowerCase().includes(lowerCaseTerm)
      );

    return {
      registerbranch: filterList(applications.registerbranch),
      requestcredit: filterList(applications.requestcredit),
    };
  };

  useEffect(() => {
    if (ApplicationsData) {
      const filteredData = searchTerm
        ? filterApplications(ApplicationsData, searchTerm)
        : ApplicationsData;
      setFilteredApplications(filteredData);
    }
  }, [ApplicationsData, searchTerm]);

  // A new search starts the table from its first page (a refetch keeps the current page).
  useEffect(() => {
    setPageForRB(1);
  }, [searchTerm]);

  // Clamp so a shrinking list (search or refetch) never leaves a table on an empty page.
  const pageCountFor = (total: number) => Math.max(1, Math.ceil(total / itemsPerPage));
  const currentPageForRB = Math.min(pageForRB, pageCountFor(filteredApplications?.registerbranch.length || 0));

  const currentRBItems = filteredApplications?.registerbranch.slice(
    (currentPageForRB - 1) * itemsPerPage,
    currentPageForRB * itemsPerPage
  );

  // ---- presentation-only derived values ----------------------------------
  const currencyText: string = FinancialDetailsData?.currency?.text ?? "";
  const termsText: string = FinancialDetailsData?.terms?.text ?? "";
  const overdueBalance = Number(FinancialDetailsData?.overdueBalance?.value) || 0;
  const isOverdue = overdueBalance > 0;

  const customerName = userData?.name?.value ?? "";
  const metaParts = [
    userData?.subsidiary?.text,
    isFinancialDetailsPending ? undefined : termsText,
  ].filter((part): part is string => Boolean(part));

  // The query can resolve one paint before the filter effect copies it into `filteredApplications` — treat that
  // unsynced frame as loading so the sub-panels never flash "0" / "Nothing here yet".
  const isAppsLoading = isApplicationDataPending || (ApplicationsData != null && filteredApplications === undefined);
  // Credit account applications live on My Contracts; the dashboard lists branch applications
  const registerBranchTotal = filteredApplications?.registerbranch.length || 0;
  const hasNoApplications = !isAppsLoading && registerBranchTotal === 0;
  const noMatchCopy = searchTerm ? `No applications match "${searchTerm}"` : "Nothing here yet";

  const newApplicationButton = (onOpen: () => void, size?: "sm") => (
    <Button variant="primary" size={size} leftIcon={<FiPlus />} onClick={onOpen}>
      New branch application
    </Button>
  );

  return (
    <Stack spacing={6}>
      <RegisterBranchApplicationViewModal
        applicationId={selectedApplication?.application_id}
        isOpen={isRegisterBranchModalOpen}
        onClose={onRegisterBranchModalClose}
      />

      <PageHeader
        variant="hero"
        eyebrow="DASHBOARD"
        title={
          <>
            Welcome <Highlight onDark>back</Highlight>
          </>
        }
        subtitle={
          userData ? (
            customerName || undefined
          ) : (
            <Skeleton
              speed={1.2}
              as="span"
              display="inline-block"
              h="1.25rem"
              w="12rem"
              verticalAlign="middle"
              startColor="navy.800"
              endColor="navy.700"
            />
          )
        }
        meta={metaParts.length > 0 ? metaParts.join(" · ") : undefined}
        actions={
          <CreateApplicationModal
            kind="registerbranch"
            refetchApplications={refetchApplications}
            disclosure={createApplication}
            renderTrigger={(onOpen) => newApplicationButton(onOpen)}
          />
        }
      >
        {/* The hero balance gets the wider column so a 10,000,000.00 amount and its currency fit on one line. */}
        <SimpleGrid templateColumns={{ base: "1fr", md: "1.6fr 1fr" }} gap={4}>
          {isFinancialDetailsPending ? (
            <>
              <StatSkeleton size="hero" />
              <StatSkeleton />
            </>
          ) : isFinancialError && !FinancialDetailsData ? (
            <Alert status="error" gridColumn="1 / -1">
              <AlertIcon />
              <Box flex="1">
                <AlertTitle>We couldn't load your account balance</AlertTitle>
              </Box>
              <Button variant="ghost" size="sm" leftIcon={<FiRotateCcw />} onClick={() => refetchFinancial()}>
                Retry
              </Button>
            </Alert>
          ) : (
            <>
              <Box>
                <MotionReveal delay={0.04} style={{ height: "100%" }}>
                  <StatCard
                    size="hero"
                    tone={isOverdue ? "danger" : "success"}
                    label="Overdue balance"
                    unit={currencyText || undefined}
                    value={overdueBalance}
                    format={formatAmount}
                    caption={isOverdue ? "Please settle to keep ordering" : "No overdue amount"}
                    badge={<StatusBadge kind="application" value={isOverdue ? "overdue" : "clear"} />}
                    h="full"
                  />
                </MotionReveal>
              </Box>
              <MotionReveal delay={0.08}>
                <StatCard label="Payment terms" value={termsText || "—"} caption="Agreed with Bidfood" h="full" />
              </MotionReveal>
            </>
          )}
        </SimpleGrid>
      </PageHeader>

      <StockAnalysis
        items={stockAnalysis.data}
        isPending={stockAnalysis.isPending}
        isError={stockAnalysis.isError}
        onRetry={() => stockAnalysis.refetch()}
      />

      <SpendAnalysis
        items={spendAnalysis.data}
        isPending={spendAnalysis.isPending}
        isError={spendAnalysis.isError}
        onRetry={() => spendAnalysis.refetch()}
        currency={FinancialDetailsData?.currency?.text ?? ""}
      />

      <SectionCard
        folio="04"
        title="My applications"
        lede="Your new branch registrations"
        isLoading={isAppsLoading}
        toolbar={
          <SearchField
            ref={searchRef}
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search applications"
            aria-label="Search applications"
          />
        }
      >
        {isApplicationsError && !ApplicationsData ? (
          <Alert status="error" alignItems="flex-start">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>We couldn't load your applications</AlertTitle>
              <AlertDescription>Check your connection and try again.</AlertDescription>
            </Box>
            <Button variant="ghost" size="sm" leftIcon={<FiRotateCcw />} onClick={() => refetchApplications()}>
              Retry
            </Button>
          </Alert>
        ) : hasNoApplications ? (
          <EmptyState
            icon={searchTerm ? FiSearch : FiInbox}
            title={searchTerm ? "No matching applications" : "No applications yet"}
            description={
              searchTerm
                ? `Nothing matches "${searchTerm}". Try another ID, status or date.`
                : "Register a new branch to get started."
            }
            action={
              searchTerm ? (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<FiRotateCcw />}
                  onClick={() => {
                    setSearchTerm("");
                    // This button unmounts once rows reappear — hand focus back to the search field.
                    searchRef.current?.focus();
                  }}
                >
                  Clear search
                </Button>
              ) : (
                newApplicationButton(createApplication.onOpen, "sm")
              )
            }
          />
        ) : (
          <Stack spacing={5}>
            {/* Register Branch Applications */}
            <Box layerStyle="cardMuted" p={4}>
              <SubPanelHeader title="New branch applications" count={registerBranchTotal} isLoading={isAppsLoading} />
              {isAppsLoading ? (
                <TableSkeleton columns={REGISTER_BRANCH_COLUMNS} hiddenColumns={[ACTIONS_COLUMN]} rows={3} />
              ) : registerBranchTotal === 0 ? (
                <Text textStyle="body.sm" color="text.muted" textAlign="center" py={6}>
                  {noMatchCopy}
                </Text>
              ) : (
                <>
                  <DataTable label="Branch applications">
                    <Table>
                      <Thead>
                        <Tr>
                          <Th scope="col">Application ID</Th>
                          <Th scope="col">Branch Name</Th>
                          <Th scope="col">Status</Th>
                          <Th scope="col">Creation Date</Th>
                          <Th scope="col">Last Update</Th>
                          <Th scope="col">
                            <VisuallyHidden>{ACTIONS_COLUMN}</VisuallyHidden>
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {currentRBItems?.map((application) => {
                          const open = () => {
                            setSelectedApplication(application);
                            onRegisterBranchModalOpen();
                          };
                          return (
                            <Tr
                              key={application.id}
                              data-clickable="true"
                              onClick={open}
                              sx={clickableRowStyles}
                            >
                              <Td fontWeight={600}>{`${application.application_id}`}</Td>
                              <Td>
                                {application.outlet_legal_name ? (
                                  application.outlet_legal_name
                                ) : (
                                  <Text as="span" color="text.muted" fontStyle="italic">
                                    Not completed yet
                                  </Text>
                                )}
                              </Td>
                              <Td>
                                <StatusBadge kind="application" value={application.status} />
                              </Td>
                              <Td fontWeight={600}>{formatShortDate(new Date(application.creation_date))}</Td>
                              <Td fontWeight={600}>{formatShortDate(new Date(application.last_update))}</Td>
                              <Td>
                                <RowActions onView={open} applicationId={application.application_id} />
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </DataTable>
                  <Pagination
                    page={currentPageForRB}
                    pageSize={itemsPerPage}
                    total={registerBranchTotal}
                    onChange={setPageForRB}
                    itemLabel="applications"
                  />
                </>
              )}
            </Box>

          </Stack>
        )}
      </SectionCard>
    </Stack>
  );
};

export default ApplicationsPage;
